import dayjs from 'dayjs';
import { message, Button, Spin, Space, Divider, Typography, Input, Row, Col, DatePicker, Switch } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import Table, { ColumnsType } from "antd/lib/table";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { switchCrawler, getVersions, updateToolVersion, notifyUsers } from "services";

interface DataType {
  key: string,
  name: string,
  title: string,
  status: string,
}

type TVersionProps = {
  version: string,
  releaseDate: string | null
}

type TVersions = {
  azinsight: TVersionProps,
  bulkchecker: TVersionProps,
  azalert: TVersionProps,
  asinfetcher: TVersionProps,
  scanez_android: TVersionProps,
  scanez_ios: TVersionProps,
  scoutoracle: TVersionProps,
  meltable: TVersionProps
}

const Versions = () => {
  const [versions, setVersions] = useState<TVersions>({
    azinsight: {
      version: "",
      releaseDate: null
    },
    bulkchecker: {
      version: "",
      releaseDate: null
    },
    azalert: {
      version: "",
      releaseDate: null
    },
    asinfetcher: {
      version: "",
      releaseDate: null
    },
    scanez_android: {
      version: "",
      releaseDate: null
    },
    scanez_ios: {
      version: "",
      releaseDate: null
    },
    scoutoracle: {
      version: "",
      releaseDate: null
    },
    meltable: {
      version: "",
      releaseDate: null
    }
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingField, setLoadingField] = useState<boolean>(false);
  const [fieldUpdate, setFieldUpdate] = useState<string>("");
  const [azCrawler, setAzCrawler] = useState<any>([]);

  const columns: ColumnsType<DataType> = [
    {
      dataIndex: 'title',
      key: 'title',
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (value, fields) =>
        <Switch
          loading={fieldUpdate === fields.name && loadingField}
          checkedChildren="Enable"
          unCheckedChildren="Disable"
          checked={value === "enabled"}
          onChange={(value) => handleOnChangeStatus(value, fields.name)}
        />
    },
  ]

  const handleOnChangeStatus = (value: any, tool: string) => {
    setFieldUpdate(tool);
    setLoadingField(true);
    const valueUpdate = value ? '1' : '0';
    const params = {
      value: valueUpdate,
      tool: tool
    };
    switchCrawler(params)
      .then(() => {
        setAzCrawler((prevState: any) => {
          const newData = prevState.map((item: any) => {
            if (item.name === tool) {
              return ({
                ...item,
                status: value ? "enabled" : "disabled"
              })
            }
            return item;
          });

          setLoadingField(false);
          return newData;
        });
        message.success("Saved successfully")
      })
      .catch(() => {
        message.success("Couldn't save successfully");
        setLoadingField(false);
      })
  }
  
  const loadData = () => {
    setLoading(true);
    getVersions()
    .then(({ data: { data: res } }: any) => 
      setVersions((prevVersions) => {
        setLoading(false);
        setAzCrawler(() => Object.keys(res.status_enabled_tasks).map((i: any) => ({
          key: `crawler-${i}`,
          name: i,
          title: i,
          status: res.status_enabled_tasks[i] ? 'enabled' : 'disabled',
        })));
        return ({
          azinsight: {
            version: res.azinsight_version.version ?? prevVersions.azinsight.version,
            releaseDate: res.azinsight_version.releaseDate && new Date(Object.values(res.azinsight_version.releaseDate)[0] as number).toLocaleDateString()
          },
          bulkchecker: {
            version: res.bulkchecker_version.version ?? prevVersions.bulkchecker.version,
            releaseDate: res.bulkchecker_version.releaseDate && new Date(Object.values(res.bulkchecker_version.releaseDate)[0] as number).toLocaleDateString()
          },
          azalert: {
            version: res.az_aleart_version.version ?? prevVersions.azalert.version,
            releaseDate: res.az_aleart_version.releaseDate && new Date(Object.values(res.az_aleart_version.releaseDate)[0] as number).toLocaleDateString()
          },
          asinfetcher: {
            version: res.asin_fetcher_version.version ?? prevVersions.asinfetcher.version,
            releaseDate: res.asin_fetcher_version.releaseDate && new Date(Object.values(res.asin_fetcher_version.releaseDate)[0] as number).toLocaleDateString()
          },
          scanez_android: {
            version: res.scan_ez_version_android.version ?? prevVersions.scanez_android.version,
            releaseDate: res.scan_ez_version_android.releaseDate && new Date(Object.values(res.scan_ez_version_android.releaseDate)[0] as number).toLocaleDateString()
          },
          scanez_ios: {
            version: res.scan_ez_version_ios.version ?? prevVersions.scanez_ios.version,
            releaseDate: res.scan_ez_version_ios.releaseDate && new Date(Object.values(res.scan_ez_version_ios.releaseDate)[0] as number).toLocaleDateString()
          },
          scoutoracle: {
            version: res.scout_oracle_version.version ?? prevVersions.scoutoracle.version,
            releaseDate: res.scout_oracle_version.releaseDate && new Date(Object.values(res.scout_oracle_version.releaseDate)[0] as number).toLocaleDateString()
          },
          meltable: {
            version: res.meltable_version.version ?? prevVersions.meltable.version,
            releaseDate: null
          }
        })
      })
    )
  };

  const handleOnUpdateVersion = (tool: keyof TVersions) => {
    setFieldUpdate(() => {
      setLoadingField(true);
      return tool;
    })
    const params = {
      tool,
      version: versions[tool].version,
      releaseDate: versions[tool].releaseDate,
    }
    updateToolVersion(params)
      .then(() => {
        message.success(`Updated version successfully`);
        setVersions((prev: any) => ({
          ...prev,
          [tool]: {
            version: versions[tool].version,
            releaseDate: versions[tool].releaseDate,
          }}))
      })
      .catch(() => {
        message.error("Can not update version")
      })
      .finally(() => setLoadingField(false))
  };

  const handleOnNotifyUsers = (tool: string) => {
    setFieldUpdate(() => {
      setLoadingField(true);
      return tool;
    })
    const params = {
      tool,
    }
    notifyUsers(params)
      .then(() => message.success("Notified users"))
      .catch(() => message.error("Can not notify users"))
      .finally(() => setLoadingField(false))
  };

  const handleOnChangeVersion = (version: string, tool: keyof TVersions) =>
    setVersions((prev) => ({ ...prev, [tool]: { ...prev[tool], version }  }))

  const handleOnPickDate = (dateString: string | string[], tool: keyof TVersions) =>
    setVersions((prev) => ({ ...prev, [tool]: { ...prev[tool], releaseDate: dateString }  }))

  useEffect(() => {
    loadData()
  }, []);

  return (
    <>
      <Helmet>
        <title>Asinzen Versions | Admin UI</title>
      </Helmet>
      <PageHeader
        className="page-header"
        title="Asinzen Versions"
        footer={
          loading ? (
            <div className="loading-page"><Spin size="large" /></div>
          ) : (
            <Space direction="vertical" style={{ width: "100%", padding: 20 }}>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>Meltable Alert</Typography.Title>
                </Divider>
                <Row>
                  <Col xs={24} md={24} lg={16}>
                    <Input
                      value={versions.meltable.version}
                      disabled={fieldUpdate === "meltable" && loadingField}
                      onChange={(e) => handleOnChangeVersion(e.target.value, "meltable")}
                      style={{ width: 'calc(100% - 100px)' }}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4}>
                  </Col>
                  <Col xs={24} md={24} lg={4} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button
                        type="primary"
                        loading={fieldUpdate === "meltable" && loadingField}
                        onClick={() => handleOnUpdateVersion("meltable")}
                      >
                        Save
                      </Button>
                      <Button
                      ghost
                      type="primary"
                        loading={fieldUpdate === "meltable" && loadingField}
                        onClick={() => handleOnNotifyUsers("meltable")}
                      >
                        Notify to users
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>AZInsight Version</Typography.Title>
                </Divider>
                <Row>
                  <Col xs={24} md={24} lg={16}>
                    <Input
                      value={versions.azinsight.version}
                      disabled={fieldUpdate === "azinsight" && loadingField}
                      onChange={(e) => handleOnChangeVersion(e.target.value, "azinsight")}
                      style={{ width: 'calc(100% - 100px)' }}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4}>
                    <label>Release Date: </label>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={dayjs(versions.azinsight.releaseDate)}
                      onChange={(_, dateString) => handleOnPickDate(dateString, "azinsight")}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button
                        type="primary"
                        loading={fieldUpdate === "azinsight" && loadingField}
                        onClick={() => handleOnUpdateVersion("azinsight")}
                      >
                        Save
                      </Button>
                      <Button
                      ghost
                      type="primary"
                        loading={fieldUpdate === "azinsight" && loadingField}
                        onClick={() => handleOnNotifyUsers("azinsight")}
                      >
                        Notify to users
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>Bulk Scan Pro Version</Typography.Title>
                </Divider>
                <Row>
                  <Col xs={24} md={24} lg={16}>
                    <Input
                      value={versions.bulkchecker.version}
                      disabled={fieldUpdate === "bulkchecker" && loadingField}
                      onChange={(e) => handleOnChangeVersion(e.target.value, "bulkchecker")}
                      style={{ width: 'calc(100% - 100px)' }}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4}>
                    <label>Release Date: </label>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={dayjs(versions.bulkchecker.releaseDate)}
                      onChange={(_, dateString) => handleOnPickDate(dateString, "bulkchecker")}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button
                        type="primary"
                        loading={fieldUpdate === "bulkchecker" && loadingField}
                        onClick={() => handleOnUpdateVersion("bulkchecker")}
                      >
                        Save
                      </Button>
                      <Button
                      ghost
                      type="primary"
                        loading={fieldUpdate === "bulkchecker" && loadingField}
                        onClick={() => handleOnNotifyUsers("bulkchecker")}
                      >
                        Notify to users
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>AZAlert Version</Typography.Title>
                </Divider>
                <Row>
                  <Col xs={24} md={24} lg={16}>
                    <Input
                      value={versions.azalert.version}
                      disabled={fieldUpdate === "azalert" && loadingField}
                      onChange={(e) => handleOnChangeVersion(e.target.value, "azalert")}
                      style={{ width: 'calc(100% - 100px)' }}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4}>
                    <label>Release Date: </label>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={dayjs(versions.azalert.releaseDate)}
                      onChange={(_, dateString) => handleOnPickDate(dateString, "azalert")}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button
                        type="primary"
                        loading={fieldUpdate === "azalert" && loadingField}
                        onClick={() => handleOnUpdateVersion("azalert")}
                      >
                        Save
                      </Button>
                      <Button
                      ghost
                      type="primary"
                        loading={fieldUpdate === "azalert" && loadingField}
                        onClick={() => handleOnNotifyUsers("azalert")}
                      >
                        Notify to users
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>ASINFetcher Version</Typography.Title>
                </Divider>
                <Row>
                  <Col xs={24} md={24} lg={16}>
                    <Input
                      value={versions.asinfetcher.version}
                      disabled={fieldUpdate === "asinfetcher" && loadingField}
                      onChange={(e) => handleOnChangeVersion(e.target.value, "asinfetcher")}
                      style={{ width: 'calc(100% - 100px)' }}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4}>
                    <label>Release Date: </label>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={dayjs(versions.asinfetcher.releaseDate)}
                      onChange={(_, dateString) => handleOnPickDate(dateString, "asinfetcher")}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button
                        type="primary"
                        loading={fieldUpdate === "asinfetcher" && loadingField}
                        onClick={() => handleOnUpdateVersion("asinfetcher")}
                      >
                        Save
                      </Button>
                      <Button
                      ghost
                      type="primary"
                        loading={fieldUpdate === "asinfetcher" && loadingField}
                        onClick={() => handleOnNotifyUsers("asinfetcher")}
                      >
                        Notify to users
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>ScanEZ Version Android</Typography.Title>
                </Divider>
                <Row>
                  <Col xs={24} md={24} lg={16}>
                    <Input
                      value={versions.scanez_android.version}
                      disabled={fieldUpdate === "scanez_android" && loadingField}
                      onChange={(e) => handleOnChangeVersion(e.target.value, "scanez_android")}
                      style={{ width: 'calc(100% - 100px)' }}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4}>
                    <label>Release Date: </label>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={dayjs(versions.scanez_android.releaseDate)}
                      onChange={(_, dateString) => handleOnPickDate(dateString, "scanez_android")}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button
                        type="primary"
                        loading={fieldUpdate === "scanez_android" && loadingField}
                        onClick={() => handleOnUpdateVersion("scanez_android")}
                      >
                        Save
                      </Button>
                      <Button
                      ghost
                      type="primary"
                        loading={fieldUpdate === "scanez_android" && loadingField}
                        onClick={() => handleOnNotifyUsers("scanez_android")}
                      >
                        Notify to users
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>ScanEZ Version iOS</Typography.Title>
                </Divider>
                <Row>
                  <Col xs={24} md={24} lg={16}>
                    <Input
                      value={versions.scanez_ios.version}
                      disabled={fieldUpdate === "scanez_ios" && loadingField}
                      onChange={(e) => handleOnChangeVersion(e.target.value, "scanez_ios")}
                      style={{ width: 'calc(100% - 100px)' }}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4}>
                    <label>Release Date: </label>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={dayjs(versions.scanez_ios.releaseDate)}
                      onChange={(_, dateString) => handleOnPickDate(dateString, "scanez_ios")}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button
                        type="primary"
                        loading={fieldUpdate === "scanez_ios" && loadingField}
                        onClick={() => handleOnUpdateVersion("scanez_ios")}
                      >
                        Save
                      </Button>
                      <Button
                      ghost
                      type="primary"
                        loading={fieldUpdate === "scanez_ios" && loadingField}
                        onClick={() => handleOnNotifyUsers("scanez_ios")}
                      >
                        Notify to users
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>ScoutOracle Sales Estimation Engine</Typography.Title>
                </Divider>
                <Row>
                  <Col xs={24} md={24} lg={16}>
                    <Input
                      value={versions.scoutoracle.version}
                      disabled={fieldUpdate === "scoutoracle" && loadingField}
                      onChange={(e) => handleOnChangeVersion(e.target.value, "scoutoracle")}
                      style={{ width: 'calc(100% - 100px)' }}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4}>
                    <label>Release Date: </label>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={dayjs(versions.scoutoracle.releaseDate)}
                      onChange={(_, dateString) => handleOnPickDate(dateString, "scoutoracle")}
                    />
                  </Col>
                  <Col xs={24} md={24} lg={4} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button
                        type="primary"
                        loading={fieldUpdate === "scoutoracle" && loadingField}
                        onClick={() => handleOnUpdateVersion("scoutoracle")}
                      >
                        Save
                      </Button>
                      <Button
                      ghost
                      type="primary"
                        loading={fieldUpdate === "scoutoracle" && loadingField}
                        onClick={() => handleOnNotifyUsers("scoutoracle")}
                      >
                        Notify to users
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
              <Space direction="vertical" style={{ width: "100%", marginBottom: 15 }}>
                <Divider orientation="left">
                  <Typography.Title level={4}>AZInsight Chrome page crawler</Typography.Title>
                </Divider>
                <Typography.Text>Crawler will scrape chrome page every 30 minutes and update the version number automatically when it detects new version</Typography.Text>
                <Table
                  columns={columns}
                  dataSource={azCrawler}
                  pagination={false}
                  showHeader={false}
                />
              </Space>
            </Space>
          )
        }
      />
    </>
  )
}

export default Versions;